import * as core from '../../core'

import * as packages from './packages'
import * as resources from './resources'

export function getAllPackages(): Generator<core.quiver.Package.WithId> {
  return _extractAll<core.quiver.Package, packages.Id>(packages, 'PACKAGE')
}

export function getPackage(id: packages.Id): core.quiver.Package.WithId
export function getPackage(id: string): core.quiver.Package.WithId | undefined
export function getPackage(id: string): core.quiver.Package.WithId | undefined {
  // NOTE: we assume that we are given a valid ID
  return _find<core.quiver.Package, packages.Id>(
    id as packages.Id,
    packages,
    'PACKAGE',
  )
}

export function getAllResources(): Generator<core.quiver.Resource.WithId> {
  return _extractAll<core.quiver.Resource, resources.Id>(resources, 'RESOURCE')
}

export function getResource(id: resources.Id): core.quiver.Resource.WithId
export function getResource(id: string): core.quiver.Resource.WithId | undefined
export function getResource(
  id: string,
): core.quiver.Resource.WithId | undefined {
  // NOTE: we assume that we are given a valid ID
  return _find<core.quiver.Resource, resources.Id>(
    id as resources.Id,
    resources,
    'RESOURCE',
  )
}

export function getAllResourceIdsIn(
  ...packageIds: readonly packages.Id[]
): readonly resources.Id[] {
  const resourceIds = new Set<resources.Id>()
  const contents = packageIds.flatMap(id => getPackage(id).contents)
  for (const content of contents) {
    const {
      package: contentPackageId,
      resource: contentResourceId,
      isExcluded,
    } = core.quiver.Package.Content.parse(content)
    const currentResourceIds = contentResourceId
      ? [contentResourceId]
      : getAllResourceIdsIn(contentPackageId)
    for (const resourceId of currentResourceIds) {
      if (isExcluded) {
        resourceIds.delete(resourceId)
      } else {
        resourceIds.add(resourceId)
      }
    }
  }
  return Array.from(resourceIds)
}

function _find<TObj, TId extends string>(
  id: TId,
  root: unknown,
  constantName: 'RESOURCE' | 'PACKAGE',
): (TObj & core.common.Identified<TId>) | undefined {
  const path = id.split('.')
  let obj = root as Unknown
  let k: string | undefined
  while ((k = path.shift())) {
    obj = obj?.[k]
    if (!obj) {
      break
    }
  }
  // NOTE: we just hope this is the right type
  const result = obj?.[constantName] as TObj | undefined
  return result && { ...result, id }
}

function* _extractAll<TObj, TId extends string>(
  root: unknown,
  constantName: 'RESOURCE' | 'PACKAGE',
): Generator<TObj & core.common.Identified<TId>> {
  interface Iteration {
    readonly id: string
    readonly obj: Unknown.ButNotNullish
  }
  const iterations: Iteration[] = [
    {
      id: '',
      obj: root as Unknown.ButNotNullish,
    },
  ]
  let iteration: Iteration | undefined
  while ((iteration = iterations.pop())) {
    const { id, obj } = iteration
    if (obj[constantName]) {
      // NOTE: we just hope this is the right type
      yield { ...(obj[constantName] as TObj), id: id as TId }
    }
    for (const k in obj) {
      if (k === k.toUpperCase()) {
        // We do not want to traverse constants (all uppercase names)
        continue
      }
      const subobj = obj[k]
      if (subobj) {
        iterations.push({
          id: id ? `${id}.${k}` : k,
          obj: subobj,
        })
      }
    }
  }
}
